import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { downloadCsv, type CsvColumn } from "@/lib/csv";
import { formatFullDate, formatNumber, formatRelativeTime } from "@/lib/format";
import type { ApiCallLog, ApiClient, ApiKey } from "@/pages/admin/apikeys/hooks/useApiKeys";

type ResultBucket = "all" | "ok" | "fail" | "blocked";
type RangeKey = "all" | "today" | "24h" | "7d";

const PREVIEW = 12;

interface ApiCallLogPanelProps {
  logs: ApiCallLog[];
  clients: ApiClient[];
  keys: ApiKey[];
  loading: boolean;
  clientFilter: number | "all";
  onClientFilterChange: (clientId: number | "all") => void;
}

const BLOCKED_REASONS = new Set([
  "rate_limited",
  "quota_exceeded",
  "key_revoked",
  "client_suspended",
  "key_expired",
  "invalid_api_key",
  "missing_api_key",
]);

function resultBucket(log: ApiCallLog): Exclude<ResultBucket, "all"> {
  if (log.valid === true) return "ok";
  if (log.status_code === 429 || (log.reason && BLOCKED_REASONS.has(log.reason))) return "blocked";
  return "fail";
}

const BUCKET_TONE: Record<Exclude<ResultBucket, "all">, string> = {
  ok: "bg-primary-100 text-primary-700",
  fail: "bg-secondary-100 text-secondary-900",
  blocked: "bg-accent-100 text-accent-800",
};

function withinRange(iso: string, range: RangeKey): boolean {
  if (range === "all") return true;
  const time = new Date(iso).getTime();
  if (Number.isNaN(time)) return false;
  if (range === "today") {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    return time >= start.getTime();
  }
  const window = range === "24h" ? 24 * 3600000 : 7 * 86400000;
  return Date.now() - time <= window;
}

// 调用日志：按对接方 / 密钥 / 结果 / 时间范围筛选校验接口的每一次调用，支持导出、
// 一键复制对接方全部密钥前缀，以及按 key_id 下钻某把密钥的独立调用明细。
export default function ApiCallLogPanel({
  logs,
  clients,
  keys,
  loading,
  clientFilter,
  onClientFilterChange,
}: ApiCallLogPanelProps) {
  const { t } = useTranslation();
  const [keyFilter, setKeyFilter] = useState<number | "all">("all");
  const [bucket, setBucket] = useState<ResultBucket>("all");
  const [range, setRange] = useState<RangeKey>("all");
  const [expanded, setExpanded] = useState(false);
  const [copiedPrefixes, setCopiedPrefixes] = useState(false);

  const clientName = useMemo(() => {
    const map = new Map<number, string>();
    clients.forEach((client) => map.set(client.id, client.name));
    return map;
  }, [clients]);

  const keyName = useMemo(() => {
    const map = new Map<number, ApiKey>();
    keys.forEach((key) => map.set(key.id, key));
    return map;
  }, [keys]);

  const keysForClient = useMemo(
    () => (clientFilter === "all" ? [] : keys.filter((key) => key.client_id === clientFilter)),
    [keys, clientFilter],
  );

  const filtered = useMemo(
    () =>
      logs.filter((log) => {
        if (clientFilter !== "all" && log.client_id !== clientFilter) return false;
        if (keyFilter !== "all" && log.key_id !== keyFilter) return false;
        if (bucket !== "all" && resultBucket(log) !== bucket) return false;
        return withinRange(log.created_at, range);
      }),
    [logs, clientFilter, keyFilter, bucket, range],
  );

  const keyDetail = useMemo(() => {
    if (keyFilter === "all") return null;
    const scoped = logs.filter(
      (log) => log.key_id === keyFilter && withinRange(log.created_at, range),
    );
    return {
      key: keyName.get(keyFilter) ?? null,
      total: scoped.length,
      ok: scoped.filter((log) => log.valid === true).length,
      blocked: scoped.filter((log) => resultBucket(log) === "blocked").length,
    };
  }, [keyFilter, logs, keyName, range]);

  const hasFilter = clientFilter !== "all" || keyFilter !== "all" || bucket !== "all" || range !== "all";
  const visible = expanded ? filtered : filtered.slice(0, PREVIEW);
  const bucketLabel: Record<Exclude<ResultBucket, "all">, string> = {
    ok: t("apikeys.logStatusOk"),
    fail: t("apikeys.logStatusFail"),
    blocked: t("apikeys.logStatusBlocked"),
  };

  const ranges: { key: RangeKey; label: string }[] = [
    { key: "all", label: t("apikeys.logRangeAll") },
    { key: "today", label: t("apikeys.logRangeToday") },
    { key: "24h", label: t("apikeys.logRange24h") },
    { key: "7d", label: t("apikeys.logRange7d") },
  ];

  const handleClientChange = (value: number | "all") => {
    onClientFilterChange(value);
    setKeyFilter("all");
  };

  const handleCopyPrefixes = async () => {
    if (keysForClient.length === 0) return;
    try {
      await navigator.clipboard.writeText(keysForClient.map((key) => key.key_prefix).join("\n"));
      setCopiedPrefixes(true);
      window.setTimeout(() => setCopiedPrefixes(false), 2000);
    } catch {
      // 剪贴板不可用时静默忽略
    }
  };

  const handleExport = () => {
    if (filtered.length === 0) return;
    const columns: CsvColumn<ApiCallLog>[] = [
      { key: "time", label: t("apikeys.colTime"), value: (row) => formatFullDate(row.created_at) },
      {
        key: "client",
        label: t("apikeys.colClient"),
        value: (row) => (row.client_id != null ? clientName.get(row.client_id) ?? t("apikeys.unknown") : t("apikeys.unknown")),
      },
      { key: "key", label: t("apikeys.colKey"), value: (row) => (row.key_id != null ? `#${row.key_id}` : "") },
      { key: "license", label: t("apikeys.colLicense"), value: (row) => row.license_key ?? "" },
      { key: "result", label: t("apikeys.colResult"), value: (row) => bucketLabel[resultBucket(row)] },
      { key: "reason", label: "reason", value: (row) => row.reason ?? "" },
      { key: "status", label: "HTTP", value: (row) => row.status_code ?? "" },
      { key: "latency", label: t("apikeys.colLatency"), value: (row) => row.latency_ms ?? "" },
      { key: "ip", label: t("apikeys.colIp"), value: (row) => row.ip ?? "" },
    ];
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`api-call-logs_${stamp}.csv`, columns, filtered);
  };

  const resetFilters = () => {
    onClientFilterChange("all");
    setKeyFilter("all");
    setBucket("all");
    setRange("all");
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
            <i className="ri-file-list-3-line" />
          </span>
          <div>
            <h2 className="font-heading text-sm font-semibold text-foreground-950">{t("apikeys.logsTitle")}</h2>
            <p className="text-[11px] text-foreground-500">{t("apikeys.logsSub")}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-background-100 px-3 py-1 text-[11px] font-medium text-foreground-700">
            <span className="flex h-3 w-3 items-center justify-center">
              <i className="ri-list-check-2" />
            </span>
            {formatNumber(filtered.length)}
          </span>
          <button
            type="button"
            disabled={filtered.length === 0}
            onClick={handleExport}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-download-2-line" />
            </span>
            {t("apikeys.logExport")}
          </button>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <select
          value={clientFilter}
          onChange={(event) => handleClientChange(event.target.value === "all" ? "all" : Number(event.target.value))}
          className="h-9 min-w-[150px] cursor-pointer rounded-md border border-background-300 bg-background-50 px-2.5 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none"
        >
          <option value="all">{t("apikeys.logClientAll")}</option>
          {clients.map((client) => (
            <option key={client.id} value={client.id}>
              {client.name}
            </option>
          ))}
        </select>

        <div className="inline-flex items-center gap-1 rounded-full border border-background-200 bg-background-100 px-1 py-1">
          {(["all", "ok", "fail", "blocked"] as ResultBucket[]).map((item) => {
            const active = bucket === item;
            return (
              <button
                key={item}
                type="button"
                onClick={() => setBucket(item)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-[11px] font-medium transition-colors ${
                  active ? "bg-secondary-500 text-background-50" : "text-foreground-600 hover:bg-background-50"
                }`}
              >
                {item === "all" ? t("apikeys.logStatusAll") : bucketLabel[item]}
              </button>
            );
          })}
        </div>

        <div className="inline-flex items-center gap-1 rounded-full border border-background-200 bg-background-100 px-1 py-1">
          {ranges.map((item) => {
            const active = range === item.key;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => setRange(item.key)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-[11px] font-medium transition-colors ${
                  active ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:bg-background-50"
                }`}
              >
                {item.label}
              </button>
            );
          })}
        </div>

        {hasFilter && (
          <button
            type="button"
            onClick={resetFilters}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs font-medium text-foreground-600 transition-colors hover:bg-background-100"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-close-circle-line" />
            </span>
            {t("apikeys.logClear")}
          </button>
        )}
      </div>

      {clientFilter !== "all" && (
        <div className="mt-4 rounded-md border border-background-200 bg-background-100 p-3.5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-foreground-700">
              <span className="flex h-3.5 w-3.5 items-center justify-center text-primary-700">
                <i className="ri-key-2-line" />
              </span>
              {t("apikeys.logKeyFilterLabel")}
            </span>
            <button
              type="button"
              disabled={keysForClient.length === 0}
              onClick={() => void handleCopyPrefixes()}
              title={keysForClient.length === 0 ? t("apikeys.logCopyPrefixesEmpty") : t("apikeys.logCopyPrefixesTitle")}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className={copiedPrefixes ? "ri-checkbox-circle-line text-primary-700" : "ri-file-copy-line"} />
              </span>
              {copiedPrefixes
                ? t("apikeys.logCopiedPrefixes", { count: keysForClient.length })
                : t("apikeys.logCopyPrefixes")}
            </button>
          </div>

          {keysForClient.length > 0 && (
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setKeyFilter("all")}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-[11px] font-medium transition-colors ${
                  keyFilter === "all"
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300 bg-background-50 text-foreground-600 hover:bg-background-50"
                }`}
              >
                {t("apikeys.logKeyAll")}
              </button>
              {keysForClient.map((key) => {
                const active = keyFilter === key.id;
                return (
                  <button
                    key={key.id}
                    type="button"
                    onClick={() => setKeyFilter(key.id)}
                    className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 text-[11px] font-medium transition-colors ${
                      active
                        ? "bg-primary-500 text-background-50"
                        : "border border-background-300 bg-background-50 text-foreground-600 hover:bg-background-50"
                    }`}
                  >
                    <span className="font-mono">{key.key_prefix}</span>
                    <span className={active ? "text-background-50/80" : "text-foreground-400"}>{key.name}</span>
                  </button>
                );
              })}
            </div>
          )}

          {keyDetail && keyDetail.key && (
            <div className="mt-3 grid gap-2 rounded-md border border-background-200 bg-background-50 p-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="min-w-0">
                <p className="text-[10px] text-foreground-500">{t("apikeys.logKeyDetailTitle")}</p>
                <p className="truncate font-mono text-xs font-medium text-foreground-900">{keyDetail.key.key_prefix}…</p>
              </div>
              <div>
                <p className="text-[10px] text-foreground-500">{t("apikeys.logKeyCalls")}</p>
                <p className="text-xs text-foreground-900">
                  {formatNumber(keyDetail.total)}
                  <span className="ml-1.5 text-primary-700">{t("apikeys.logStatusOk")} {formatNumber(keyDetail.ok)}</span>
                  {keyDetail.blocked > 0 && (
                    <span className="ml-1.5 text-accent-700">{t("apikeys.logStatusBlocked")} {formatNumber(keyDetail.blocked)}</span>
                  )}
                </p>
              </div>
              <div>
                <p className="text-[10px] text-foreground-500">{t("apikeys.keyRate")} / {t("apikeys.keyQuota")}</p>
                <p className="text-xs text-foreground-900">
                  {t("apikeys.rateUnit", { count: formatNumber(keyDetail.key.rate_limit_per_min) })} ·{" "}
                  {keyDetail.key.quota_per_day > 0
                    ? t("apikeys.quotaUnit", { count: formatNumber(keyDetail.key.quota_per_day) })
                    : t("apikeys.keyUnlimited")}
                </p>
              </div>
              <div>
                <p className="text-[10px] text-foreground-500">{t("apikeys.keyLastUsed")}</p>
                <p className="text-xs text-foreground-900">
                  {keyDetail.key.last_used_at ? formatRelativeTime(keyDetail.key.last_used_at) : t("apikeys.keyNever")}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="mt-4 flex flex-col gap-2">
          {[0, 1, 2, 3].map((item) => (
            <div key={item} className="h-10 rounded-md bg-background-100" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="mt-4 flex flex-col items-center gap-2 rounded-md border border-dashed border-background-300 bg-background-100 py-10 text-center">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-background-200 text-foreground-500">
            <i className="ri-file-list-3-line text-lg" />
          </span>
          <p className="text-sm font-medium text-foreground-800">
            {logs.length === 0 ? t("apikeys.logEmpty") : t("apikeys.logNoMatch")}
          </p>
          <p className="text-xs text-foreground-500">
            {logs.length === 0 ? t("apikeys.logEmptyDesc") : t("apikeys.logNoMatchDesc")}
          </p>
        </div>
      ) : (
        <>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[840px] text-sm">
              <thead>
                <tr className="bg-background-100 text-left text-[11px] font-medium text-foreground-600">
                  <th className="px-3 py-3">{t("apikeys.colTime")}</th>
                  <th className="px-3 py-3">{t("apikeys.colClient")}</th>
                  <th className="px-3 py-3">{t("apikeys.colKey")}</th>
                  <th className="px-3 py-3">{t("apikeys.colLicense")}</th>
                  <th className="px-3 py-3">{t("apikeys.colResult")}</th>
                  <th className="px-3 py-3 text-right">{t("apikeys.colLatency")}</th>
                  <th className="px-3 py-3">{t("apikeys.colIp")}</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((log) => (
                  <tr key={log.id} className="border-t border-background-200">
                    <td className="px-3 py-2.5">
                      <p className="text-xs text-foreground-900">{formatFullDate(log.created_at)}</p>
                      <p className="text-[11px] text-foreground-400">{formatRelativeTime(log.created_at)}</p>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-foreground-800">
                      {log.client_id != null ? clientName.get(log.client_id) ?? t("apikeys.unknown") : t("apikeys.unknown")}
                    </td>
                    <td className="px-3 py-2.5 font-mono text-[11px] text-foreground-600">
                      {log.key_id != null ? keyName.get(log.key_id)?.key_prefix ?? `#${log.key_id}` : "—"}
                    </td>
                    <td className="px-3 py-2.5 font-mono text-[11px] text-foreground-600">
                      {log.license_key ?? "—"}
                    </td>
                    <td className="px-3 py-2.5">
                      <span
                        className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${BUCKET_TONE[resultBucket(log)]}`}
                      >
                        {bucketLabel[resultBucket(log)]}
                      </span>
                      {log.reason && (
                        <span className="ml-1.5 font-mono text-[10px] text-foreground-400">{log.reason}</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right text-[11px] text-foreground-600">
                      {log.latency_ms != null ? t("apikeys.latencyUnit", { ms: log.latency_ms }) : "—"}
                    </td>
                    <td className="px-3 py-2.5 font-mono text-[11px] text-foreground-500">{log.ip ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filtered.length > PREVIEW && (
            <div className="mt-3 flex justify-center">
              <button
                type="button"
                onClick={() => setExpanded((value) => !value)}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100"
              >
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className={expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
                </span>
                {expanded ? t("apikeys.logCollapse") : t("apikeys.logShowAll", { count: filtered.length })}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}